import { z } from 'zod';

import { ApiError } from '@mds/shared/lib/api';
import { isChoiceType } from '@mds/shared/lib/form-answers';
import type {
  FormQuestion,
  FormQuestionInput,
  FormQuestionSet,
  FormQuestionType,
  FormSectionInput,
} from '@mds/shared/types/form-question';

import { optionalText, requiredText } from '@/lib/validation';

/**
 * The working copy behind every event form builder — registration and feedback — and the
 * conversions either side of it.
 *
 * The API replaces a whole form atomically and hands back fresh IDs after every save, so the
 * builder never sends an ID: each row carries a local key for React and nothing else.
 */

export const QUESTION_TYPE_LABELS: Record<FormQuestionType, string> = {
  text: 'Short answer',
  textarea: 'Long answer',
  select: 'Dropdown',
  multiselect: 'Multi-select',
  checkbox: 'Checkboxes',
  radio: 'Single choice',
  number: 'Number',
  rating: 'Rating',
  date: 'Date',
};

export interface QuestionDraft {
  key: string;
  label: string;
  type: FormQuestionType;
  /** One option per line, as typed. Only sent for choice types. */
  options: string;
  required: boolean;
}

export interface SectionDraft {
  key: string;
  title: string;
  description: string;
  questions: QuestionDraft[];
}

/** The questions of a form being edited. A form's own switches sit beside these. */
export interface QuestionSetDraft {
  ungrouped: QuestionDraft[];
  sections: SectionDraft[];
}

let counter = 0;
export const localKey = () => `k${++counter}`;

export const blankQuestion = (): QuestionDraft => ({
  key: localKey(),
  label: '',
  type: 'text',
  options: '',
  required: false,
});

export const blankSection = (): SectionDraft => ({
  key: localKey(),
  title: '',
  description: '',
  questions: [blankQuestion()],
});

const questionDraftOf = (q: FormQuestion): QuestionDraft => ({
  key: localKey(),
  label: q.label,
  type: q.type,
  options: q.options.join('\n'),
  required: q.required,
});

export const questionSetDraftOf = (form: FormQuestionSet): QuestionSetDraft => ({
  ungrouped: form.ungroupedQuestions.map(questionDraftOf),
  sections: form.sections.map((s) => ({
    key: localKey(),
    title: s.title,
    description: s.description,
    questions: s.questions.map(questionDraftOf),
  })),
});

const optionsOf = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const questionInputOf = (q: QuestionDraft): FormQuestionInput => ({
  label: q.label.trim(),
  type: q.type,
  options: isChoiceType(q.type) ? optionsOf(q.options) : [],
  required: q.required,
});

/** The question half of a replace request. */
export const questionSetInputOf = (
  draft: QuestionSetDraft,
): { ungroupedQuestions: FormQuestionInput[]; sections: FormSectionInput[] } => ({
  ungroupedQuestions: draft.ungrouped.map(questionInputOf),
  sections: draft.sections.map((s) => ({
    title: s.title.trim(),
    description: s.description.trim(),
    questions: s.questions.map(questionInputOf),
  })),
});

export const questionCount = (draft: {
  ungrouped: readonly unknown[];
  sections: readonly { questions: readonly unknown[] }[];
}) => draft.ungrouped.length + draft.sections.reduce((n, s) => n + s.questions.length, 0);

/** One question. Options count only for a choice type; the rest send none. */
const questionSchema = z
  .object({ label: requiredText(300), type: z.string(), options: z.string() })
  .superRefine((q, ctx) => {
    if (!isChoiceType(q.type as FormQuestionType)) return;
    const options = optionsOf(q.options);
    const issue = (message: string) => ctx.addIssue({ code: 'custom', path: ['options'], message });
    if (options.length < 2) issue('List at least two options, one per line.');
    else if (options.length > 20) issue('Up to 20 options.');
    else if (options.some((o) => o.length > 200))
      issue('Keep each option to 200 characters or fewer.');
    else if (new Set(options.map((o) => o.toLocaleLowerCase())).size !== options.length)
      issue('Each option should appear once.');
  });

/**
 * What the API would reject, checked here so it fails before the round trip. Mirrors the shared
 * question schemas in `backend/src/modules/event-forms`. The hundred-question cap spans the whole
 * form, so it is filed at `count` rather than against any one list.
 */
export const questionSetSchema = z
  .object({
    ungrouped: z.array(questionSchema),
    sections: z
      .array(
        z.object({
          title: requiredText(200),
          description: optionalText(1_000),
          questions: z
            .array(questionSchema)
            .min(1, 'Add a question to this section, or remove the section.'),
        }),
      )
      .max(20, 'Up to 20 sections.'),
  })
  .superRefine((draft, ctx) => {
    const count = questionCount(draft);
    if (count > 100) {
      ctx.addIssue({
        code: 'custom',
        path: ['count'],
        message: `A form can have at most 100 questions. Remove ${count - 100}.`,
      });
    }
  });

/** The `code` an API error carries, if it is one. */
export const errorCode = (error: unknown): string | null =>
  error instanceof ApiError ? (error.code ?? null) : null;
